import React, { useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const TOKEN_META = typeof document !== 'undefined'
  ? document.querySelector('meta[name="csrf-token"]')
  : null;
const CSRF_TOKEN = TOKEN_META ? TOKEN_META.getAttribute('content') : null;

const REQUEST_TIMEOUT_MS = 8000;

const initialState = {
  messages: [],
  pendingInput: '',
  loading: false,
  error: null,
  completed: false,
  extracted: {},
  turnLimitReached: false,
  applying: false,
  applied: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, pendingInput: action.value };
    case 'REQUEST_START':
      return { ...state, loading: true, error: null };
    case 'REQUEST_OK':
      return {
        ...state,
        loading: false,
        pendingInput: '',
        messages: [
          ...state.messages,
          { role: 'user', content: action.userContent },
          {
            role: 'assistant',
            content: action.payload.assistant_message,
            quickReplies: action.payload.quick_replies,
          },
        ],
        extracted: action.payload.extracted_so_far || state.extracted,
        completed: action.payload.completed,
      };
    case 'REQUEST_FAIL':
      return { ...state, loading: false, error: action.error };
    case 'TURN_LIMIT':
      return { ...state, loading: false, turnLimitReached: true, error: 'turn_limit_exceeded' };
    case 'APPLY_START':
      return { ...state, applying: true, error: null };
    case 'APPLY_OK':
      return { ...state, applying: false, applied: true };
    case 'APPLY_FAIL':
      return { ...state, applying: false, error: action.error };
    default:
      return state;
  }
}

function OnboardingChat(props) {
  const { companyId, conversationPath, messagesPath, tutorialPath, initialAssistantMessage } = props;
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    messages: initialAssistantMessage
      ? [{ role: 'assistant', content: initialAssistantMessage }]
      : [],
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [state.messages.length]);

  const client = axios.create({
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
      'X-CSRF-Token': CSRF_TOKEN,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  const sendMessage = (content) => {
    const trimmed = (content || '').trim();
    if (!trimmed || state.loading || state.completed) return;

    dispatch({ type: 'REQUEST_START' });
    client
      .post(messagesPath, { message: { content: trimmed } })
      .then((res) => {
        dispatch({ type: 'REQUEST_OK', userContent: trimmed, payload: res.data });
      })
      .catch((err) => {
        const code = err.response && err.response.data && err.response.data.error;
        if (code === 'turn_limit_exceeded') {
          dispatch({ type: 'TURN_LIMIT' });
        } else if (code === 'ai_response_invalid') {
          dispatch({ type: 'REQUEST_FAIL', error: 'ai_response_invalid' });
        } else {
          dispatch({ type: 'REQUEST_FAIL', error: 'network' });
        }
      });
  };

  const applySettings = () => {
    dispatch({ type: 'APPLY_START' });
    client
      .patch(conversationPath, { onboarding_session: { status: 'applied' } })
      .then(() => {
        dispatch({ type: 'APPLY_OK' });
        if (tutorialPath) {
          window.location.assign(tutorialPath);
        }
      })
      .catch(() => {
        dispatch({ type: 'APPLY_FAIL', error: 'apply_failed' });
      });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(state.pendingInput);
  };

  return (
    <div className="onboarding-chat" data-company-id={companyId}>
      <div
        ref={scrollRef}
        className="onboarding-chat__messages border rounded p-3 mb-3"
        style={{ height: 400, overflowY: 'auto', background: '#f8f9fa' }}
      >
        {state.messages.map((m, idx) => (
          <MessageBubble key={idx} message={m} onQuickReply={sendMessage} disabled={state.loading || state.completed} />
        ))}
        {state.loading && (
          <div className="text-muted small">
            <em>考えています…</em>
          </div>
        )}
      </div>

      {state.error && state.error !== 'turn_limit_exceeded' && (
        <div className="alert alert-warning">
          うまく聞き取れませんでした。もう一度お試しください。
        </div>
      )}

      {state.turnLimitReached && (
        <div className="alert alert-danger">
          会話が長くなりすぎました。お手数ですが、従来のフォームから設定してください。
        </div>
      )}

      {!state.completed && !state.turnLimitReached && (
        <form onSubmit={onSubmit} className="d-flex">
          <input
            type="text"
            className="form-control me-2"
            value={state.pendingInput}
            onChange={(e) => dispatch({ type: 'SET_INPUT', value: e.target.value })}
            disabled={state.loading}
            placeholder="メッセージを入力…"
            aria-label="メッセージ入力"
          />
          <button type="submit" className="btn btn-primary" disabled={state.loading || !state.pendingInput.trim()}>
            送信
          </button>
        </form>
      )}

      {state.completed && !state.applied && (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-success btn-lg"
            onClick={applySettings}
            disabled={state.applying}
          >
            {state.applying ? '反映中…' : 'この内容で進める'}
          </button>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message, onQuickReply, disabled }) {
  const isAssistant = message.role === 'assistant';
  return (
    <div className={`d-flex mb-2 ${isAssistant ? 'justify-content-start' : 'justify-content-end'}`}>
      <div
        className={`px-3 py-2 rounded ${isAssistant ? 'bg-white border' : 'bg-primary text-white'}`}
        style={{ maxWidth: '80%' }}
      >
        <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
        {isAssistant && Array.isArray(message.quickReplies) && message.quickReplies.length > 0 && (
          <div className="mt-2 d-flex flex-wrap" style={{ gap: 8 }}>
            {message.quickReplies.map((qr, i) => (
              <button
                key={i}
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => onQuickReply(qr)}
                disabled={disabled}
              >
                {qr}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    quickReplies: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onQuickReply: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

OnboardingChat.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  conversationPath: PropTypes.string.isRequired,
  messagesPath: PropTypes.string.isRequired,
  tutorialPath: PropTypes.string,
  initialAssistantMessage: PropTypes.string,
};

export default OnboardingChat;
